'use client'
import { createContext, useContext, useEffect, useState } from 'react'
import Lenis from 'lenis'

/**
 * Site-wide smooth scroll.
 *
 * Lenis hijacks the wheel + touch events and re-issues `window.scrollTo` on
 * each rAF tick with a smoothed offset. That has two useful side-effects:
 *
 *   1. Native scroll feels noticeably softer (the "premium agency site" feel).
 *   2. framer-motion's `useScroll` keeps working unchanged — it reads
 *      `window.scrollY`, which Lenis updates each tick. So every existing
 *      scroll-driven animation (e.g. the Hero parallax) inherits the
 *      smoothing for free.
 *
 * The live instance is published through context so add-ons that need to talk
 * to the same scroll (e.g. the homepage section-snapping in ScrollSnap) can
 * reach it via `useLenis()` instead of spinning up a second Lenis.
 *
 * Honors prefers-reduced-motion by short-circuiting the init — native scroll
 * is what the user has asked for, so anything we layer on would be hostile.
 * When it short-circuits, the `lenis` class is never added, so globals.css
 * keeps its native `scroll-behavior: smooth` fallback for that user.
 */
const LenisContext = createContext<Lenis | null>(null)

export function useLenis() {
  return useContext(LenisContext)
}

export default function LenisProvider({ children }: { children: React.ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    // Native CSS smooth-scroll fights Lenis: when Lenis sets scrollTop each
    // frame (including during snap/anchor scrollTo), the browser tries to
    // animate its own way there, so the glide collapses into an abrupt jump.
    // The globals.css `html.lenis` rule handles this, but set it inline too —
    // inline style can't be stripped/cached-stale by the bundler, so the fix
    // holds the instant Lenis mounts. Restored on cleanup.
    const root = document.documentElement
    const prevScrollBehavior = root.style.scrollBehavior
    root.style.scrollBehavior = 'auto'

    const instance = new Lenis({
      // 1.4 s duration with an exponential-decay ease gives the heavy,
      // glide-y feel that Apple product pages have. Lower this if the
      // scroll starts to feel laggy on input; raise it for more inertia.
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    })

    let rafId = 0
    const raf = (time: number) => {
      instance.raf(time)
      rafId = requestAnimationFrame(raf)
    }
    rafId = requestAnimationFrame(raf)

    // Crossfade the instant "far jump" instead of teleporting. A cream veil
    // fades up, the immediate scroll happens behind it (so the warp across the
    // pinned "Why" filmstrip is hidden), then it fades back. `jump()` is the one
    // entry point for every immediate scroll (#top and far anchor jumps), so the
    // veil always covers them.
    const FADE_MS = 220
    const veil = document.createElement('div')
    veil.setAttribute('aria-hidden', 'true')
    veil.style.cssText =
      `position:fixed;inset:0;z-index:9999;background:var(--color-surface);` +
      `opacity:0;pointer-events:none;transition:opacity ${FADE_MS}ms ease`
    document.body.appendChild(veil)

    const jump = (to: number | HTMLElement) => {
      veil.style.opacity = '1'
      window.setTimeout(() => {
        instance.scrollTo(to, { immediate: true })
        veil.style.opacity = '0'
      }, FADE_MS)
    }

    // Re-route in-page anchor clicks (nav links, "back to top") through Lenis.
    // Native CSS smooth-scroll is off while Lenis runs (see globals.css), so
    // without this those links would jump instantly.
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest?.('a[href^="#"]') as
        | HTMLAnchorElement
        | null
      if (!anchor) return
      const hash = anchor.getAttribute('href')
      if (!hash || hash === '#') return
      const target = document.querySelector(hash)
      if (!target) return
      e.preventDefault()
      // Back-to-top jumps instantly. Smooth-scrolling all the way up would drag
      // the cursor back through the tall pinned "Why" section and reverse-play
      // its scroll-scrubbed filmstrip the whole way — jarring. An immediate jump
      // snaps the scrub to its start instead of rewinding it.
      if (hash === '#top') {
        jump(0)
        return
      }
      // No manual offset: Lenis already subtracts the page's
      // `scroll-padding-top: 6rem` (globals.css) when scrolling to an element,
      // so the fixed navbar is already cleared. Passing an extra -96 here
      // stacked a second bar's worth on top (and a third on #products, which
      // also carried a scroll-mt), landing sections well below their headers.
      //
      // Long jumps (the CTA → #contact, or #faq from the hero) would otherwise
      // smooth-scroll across the tall pinned "Why" filmstrip and fast-forward
      // its scroll-scrubbed animation the whole way down — a warp-speed blur.
      // Past ~2 viewports, snap immediately instead (mirrors the #top case).
      const distance = Math.abs((target as HTMLElement).getBoundingClientRect().top)
      const far = distance > window.innerHeight * 2
      if (far) {
        jump(target as HTMLElement)
      } else {
        instance.scrollTo(target as HTMLElement)
      }
    }
    document.addEventListener('click', onClick)

    setLenis(instance)

    return () => {
      document.removeEventListener('click', onClick)
      cancelAnimationFrame(rafId)
      instance.destroy()
      veil.remove()
      root.style.scrollBehavior = prevScrollBehavior
      setLenis(null)
    }
  }, [])

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>
}
